/**
 * `InstallMethodTabs` — ADR-0042 §2 + ADR-0049 + ADR-0050. The install-
 * method selector inside the Install + verify step. Detection-preselects
 * an initial tab, but EVERY TAB IS ALWAYS CLICKABLE — detection is a
 * hint, never a gate, encoded as IA structure.
 *
 * Tabs and their bodies (ADR-0050 demotes `custom_embed` from a peer
 * tab to a disclosed fallback inside the `script_tag` panel — the
 * server-side ingest path is real but its body is a header for an
 * unwired skill surface, so it stops being one of four false-peers and
 * becomes a "Can't add a script tag?" disclosure under the primary
 * paste):
 *
 *   - `script_tag`   → `ScriptTagInstallPanel` (universal default; the
 *                      disclosure for the manual-ingest fallback lives
 *                      inside this panel)
 *   - `wordpress`    → `WordPressInstallPanel` (recommends the ADR-0188
 *                       Sill WP plugin — one upload serves the card +
 *                       proof + logs traffic; the script paste is a
 *                       demoted fallback. ADR-0193)
 *   - `woocommerce`  → `WooCommerceInstallPanel` (explicit two-step:
 *                       discovery/domain-proof snippet + REST-API
 *                       connect). Split from the shared WP tab
 *                       2026-07-08 (founder live-onboarding feedback)
 *                       — the combined "WordPress / WooCommerce" tab
 *                       conflated two separate install paths and hid
 *                       Woo's two-commit shape.
 *   - `shopify`      → `ShopifyConnectPanel` (OAuth + post-OAuth poll)
 *
 * The `InstallMethod` union still includes `'custom_embed'` for
 * forward-compatibility with any future re-promotion + so the existing
 * `preselectTab` mapping stays a pure function over the closed wire
 * enum. It is simply not surfaced as a peer-tab entry.
 *
 * Detection-preselect rule (`preselectTab`) is exported as a pure
 * helper for unit testing. The component honors the merchant's tab
 * click as authoritative — a late-arriving detection that disagrees
 * with the merchant's choice does NOT clobber the current selection.
 * The `other_known` detection case now maps to `'script_tag'` (the
 * universal default — what the prior `OtherPlatformPanel` said in copy
 * anyway).
 */

import { ArrowRight, Cloud, Code, Globe, Package, Play, ShoppingBag } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { Site } from '@sill/types';
import type { DetectPlatformResult } from '@sill/types/api';

import { useC } from '../../lib/theme.js';
import type { UseProofCheckState } from '../../hooks/useProofCheck.js';
import { AgentReadyPluginDownload } from './AgentReadyPluginDownload.js';
import { CloudflareInstallPanel } from './CloudflareInstallPanel.js';
import { ManualInstallSteps } from './ManualInstallSteps.js';
import { ScriptTagInstallPanel } from './ScriptTagInstallPanel.js';
import { redirectHostFromHints } from '../../lib/agent-card-redirect.js';
import { InstallCoverageChips } from './InstallCoverageChips.js';
import { SHOPIFY_SAFE_WORKER_AVAILABLE, WORKER_DOES_BODY } from './install-copy.js';
import { WorkerStepOne } from './WorkerStepOne.js';
import { ShopifyConnectPanel } from './ShopifyConnectPanel.js';
import { ShopifyCloudflareObserveStep } from './ShopifyDiscoverySteps.js';
import { WooCommerceInstallPanel } from './WooCommerceInstallPanel.js';
import { WordPressInstallPanel } from './WordPressInstallPanel.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

/**
 * ADR-0197 — install-picker presentation mode.
 *   - `'model'` (default): detection → a recommended one-click action
 *     per detected setup, with the always-visible manual below. This is
 *     the shipped experience for a new merchant.
 *   - `'tabs'`: the classic four-peer tab picker (ADR-0042 §2), kept as
 *     the fallback reached via "See all install options" and pinned by
 *     the pre-existing tab tests.
 */
export type InstallPresentation = 'model' | 'tabs';

export type InstallMethod =
  | 'script_tag'
  | 'wordpress'
  | 'woocommerce'
  | 'shopify'
  | 'cloudflare'
  | 'custom_embed';

export interface InstallMethodTabsProps {
  /** Detection result from `POST /v1/sites/:id/detect-platform`. If
   *  present, drives the INITIAL selected tab via `preselectTab`. If
   *  absent (loading / failed / dormant), defaults to `'script_tag'`
   *  (the universally-applicable default). Detection failure NEVER
   *  disables a tab — every tab stays clickable. */
  detected: DetectPlatformResult | null;
  /** Current site, for snippet keying + OAuth init URL building. */
  site: Site;
  /** Called when the user switches tabs (or when the initial preselect
   *  fires). The merchant's click is the authoritative selection — the
   *  IA NEVER gates the connect path on detection outcome. */
  onTabChange?: (tab: InstallMethod) => void;
  /** Relocated demo link — rendered top-right of the tab row (founder
   *  2026-07-16: move "watch an agent" into the install box's corner). */
  onWatchDemo?: () => void;
  /** Forwarded to InstallDetector for tabs that surface it. Fires once
   *  when the first agent ping arrives on the embed surface. */
  onInstalled?: () => void;
  /** Shared proof-check state from `useProofCheck`. The `script_tag`
   *  panel threads it into the snippet (for the `data-proof-token`
   *  attribute) AND renders the inline Verify control next to the
   *  snippet (ADR-0050 §1.2 paste-once / verify-once). Optional only
   *  to preserve compile-time compatibility with callers that pre-date
   *  ADR-0050 — production callers (`InstallVerifyStep`) ALWAYS pass
   *  it. */
  proofCheck?: UseProofCheckState;
  /** ADR-0197 — presentation mode. Defaults to `'model'` (the shipped
   *  detection-driven experience). `InstallVerifyStep` passes this
   *  through; the tab-focused test suites pin `'tabs'` to keep the
   *  classic-picker assertions honest. */
  presentation?: InstallPresentation;
  /** Called when a Cloudflare Worker deploy response includes
   *  `domain_verified: true` (server auto-recorded proof). Caller
   *  uses this to advance the onboarding step without requiring a
   *  separate manual Verify click. */
  onDomainVerified?: () => void;
}

interface TabSpec {
  readonly id: Exclude<InstallMethod, 'custom_embed'>;
  readonly label: string;
  readonly icon: LucideIcon;
}

const TABS: readonly TabSpec[] = [
  { id: 'script_tag', label: 'Script tag', icon: Code },
  { id: 'wordpress', label: 'WordPress', icon: Globe },
  // Split from the shared WP tab 2026-07-08 — WooCommerce carries an
  // extra REST-API connect step past the script paste, so it earns its
  // own tab with an explicit two-step body. WP-detected sites still
  // land on `wordpress`; only `woocommerce` detection routes here.
  { id: 'woocommerce', label: 'WooCommerce', icon: Package },
  { id: 'shopify', label: 'Shopify', icon: ShoppingBag },
  // ADR-0189 — one-click Worker deploy for Cloudflare-proxied sites.
  // No backend detection for CF proxy yet; tab is always clickable
  // (detection is a hint, per ADR-0042 §2).
  // ADR-0212 — labelled as a METHOD, not a platform peer of Shopify /
  // WordPress: the Worker installs the observation layer and never
  // replaces a plugin or an app.
  { id: 'cloudflare', label: 'Cloudflare Worker', icon: Cloud },
];

/**
 * The single install method Sill recommends for a detected setup — the
 * tab that pre-selects on mount AND carries the "RECOMMENDED" badge in
 * the always-visible tab bar (ADR-0197 tabs-on-top layout). Pure;
 * exported for unit testing.
 *
 * Cloudflare is a *property*, not just a platform: a Cloudflare-proxied
 * site whose origin platform we couldn't pin (unknown / other_known —
 * e.g. the vbout.com case where CF bot-protection blocks the origin
 * read, ADR-0197 / #546) recommends the Worker, which serves the card on
 * the merchant's own domain regardless of origin platform. A detected
 * store/CMS keeps its own tab (those panels are plugin/app-first and
 * already serve the static card).
 */
export function recommendedTab(
  detected: DetectPlatformResult | null,
): Exclude<InstallMethod, 'custom_embed'> {
  if (detected === null) return 'script_tag';
  const cf = detected.cloudflare_proxied === true;
  // ADR-0212 Rule 1 — tabs are what the site runs on, and the platform
  // tab always has the complete answer. WordPress / WooCommerce behind
  // Cloudflare stay on their own tab; the Worker swaps into that tab's
  // Step 1 (slice C) rather than pulling the badge to "Cloudflare Worker".
  switch (detected.platform) {
    case 'shopify':
      return 'shopify';
    case 'woocommerce':
      return 'woocommerce';
    case 'wordpress':
      return 'wordpress';
    case 'cloudflare':
      return 'cloudflare';
    case 'other_known':
    case 'unknown':
    default:
      return cf ? 'cloudflare' : 'script_tag';
  }
}

/**
 * Map a detection result to the tab that pre-selects on initial mount.
 * Delegates to {@link recommendedTab} so the pre-selected tab and the
 * badged tab are always the same one. Pure; exported for unit testing.
 */
export function preselectTab(
  detected: DetectPlatformResult | null,
): InstallMethod {
  return recommendedTab(detected);
}

export function InstallMethodTabs({
  detected,
  site,
  onTabChange,
  onWatchDemo,
  onInstalled,
  proofCheck,
  presentation = 'tabs',
  onDomainVerified,
}: InstallMethodTabsProps): JSX.Element {
  const C = useC();
  // ADR-0197 — presentation state seeds from the prop so the "See all
  // install options" affordance can locally switch model → tabs without
  // the parent re-plumbing. The prop wins on remount.
  const [presentationState, setPresentationState] =
    useState<InstallPresentation>(presentation);
  useEffect(() => {
    setPresentationState(presentation);
  }, [presentation]);
  const [active, setActive] = useState<InstallMethod>(() => preselectTab(detected));
  // Persist the CF-Worker deployed state across tab switches so the merchant
  // doesn't lose their progress when they briefly visit another tab.
  const [cfDeployed, setCfDeployed] = useState(false);
  const [cfZoneName, setCfZoneName] = useState<string | null>(null);
  // ADR-0212 Rule 2 — on an orange-clouded WordPress / WooCommerce site
  // the Worker takes Step 1 inside the platform tab. `preferPlugin` is
  // the merchant's escape hatch (one decision per site, survives tab
  // switches); once the Worker is deployed Step 1 always shows it.
  const [preferPlugin, setPreferPlugin] = useState(false);
  const cfProxied = detected?.cloudflare_proxied === true;
  const showWorkerStep1 = cfProxied && (cfDeployed || !preferPlugin);
  // ONE Worker element for the Cloudflare tab and the nested Step 1 —
  // only one is ever mounted (tab switch unmounts), and the lifted
  // deployed/zone state is what keeps them agreeing.
  const workerPanel = (nested: boolean): JSX.Element => (
    <CloudflareInstallPanel
      site={site}
      nested={nested}
      initialDeployed={cfDeployed}
      initialZoneName={cfZoneName}
      onDeployed={(zn) => { setCfDeployed(true); setCfZoneName(zn); }}
      {...(proofCheck !== undefined ? { proofCheck } : {})}
      {...(onDomainVerified !== undefined ? { onDomainVerified } : {})}
    />
  );
  /** Once the merchant clicks a tab, their choice is authoritative — a
   *  late-arriving detection that disagrees MUST NOT clobber the
   *  selection. The ref tracks "merchant has acted", not "current tab". */
  const merchantClickedRef = useRef(false);
  /** Track the last detected platform we observed so we only re-
   *  preselect on a CHANGE (initial null → result), never on identical
   *  re-renders. */
  const lastDetectedRef = useRef<DetectPlatformResult | null>(detected);

  // When detection lands AFTER mount (the typical case — the call is
  // async), silently re-preselect ONLY if the merchant hasn't already
  // switched tabs.
  useEffect(() => {
    if (lastDetectedRef.current === detected) return;
    lastDetectedRef.current = detected;
    if (merchantClickedRef.current) return;
    const next = preselectTab(detected);
    setActive(next);
    onTabChange?.(next);
  }, [detected, onTabChange]);

  const switchTo = (tab: InstallMethod): void => {
    merchantClickedRef.current = true;
    setActive(tab);
    onTabChange?.(tab);
  };

  // ADR-0197 model presentation — detected-setup → recommended
  // one-click action(s) + always-visible manual. "See all install
  // options" drops to the classic tabs picker below.
  if (presentationState === 'model') {
    return (
      <InstallModel
        detected={detected}
        site={site}
        {...(proofCheck !== undefined ? { proofCheck } : {})}
        {...(onInstalled !== undefined ? { onInstalled } : {})}
        {...(onWatchDemo !== undefined ? { onWatchDemo } : {})}
        {...(onDomainVerified !== undefined ? { onDomainVerified } : {})}
        onSeeAllOptions={() => setPresentationState('tabs')}
      />
    );
  }

  // ADR-0197 tabs-on-top — the one tab Sill recommends for the detected
  // setup carries a lime "RECOMMENDED" badge so the merchant sees the
  // suggested path at a glance while every tab stays one click away. Only
  // when detection actually ran (null → no badge; the bare script_tag
  // default isn't a "recommendation").
  const recommended = detected !== null ? recommendedTab(detected) : null;

  return (
    <div>
      <div
        className="flex flex-wrap items-center gap-1 mb-4"
        role="tablist"
        aria-label="Install method"
      >
        {TABS.map((t) => {
          const isActive = t.id === active;
          const isRecommended = t.id === recommended;
          const Icon = t.icon;
          return (
            <button
              type="button"
              key={t.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`install-tab-panel-${t.id}`}
              onClick={() => switchTo(t.id)}
              className="flex items-center gap-1.5 px-3 py-1.5"
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                fontWeight: 500,
                color: isActive ? C.text : C.muted,
                background: isActive ? C.surface2 : 'transparent',
                border: `1px solid ${
                  isRecommended
                    ? C.limeFg
                    : isActive
                      ? C.border
                      : 'transparent'
                }`,
                borderRadius: 2,
                cursor: 'pointer',
              }}
              data-testid={`install-tab-${t.id}`}
            >
              <Icon size={12} />
              {t.label}
              {isRecommended ? (
                <span
                  data-testid={`install-tab-recommended-${t.id}`}
                  style={{
                    fontFamily: monoFont,
                    fontSize: 8,
                    letterSpacing: 1,
                    // Lime chip + ink glyph reads in BOTH themes — the
                    // previous `limeFg` background is a dark olive in
                    // light mode, which rendered a near-black chip
                    // (founder 2026-08-04 screenshot).
                    color: C.ink,
                    background: C.lime,
                    borderRadius: 2,
                    padding: '1px 4px',
                    marginLeft: 2,
                  }}
                >
                  ✦
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {/* Founder 2026-10-21 — the "We think this is …" lead line was
          deleted entirely. The lime recommended marker on the tab is the
          whole hint; a sentence restating it is redundant, and the
          "We couldn’t detect a platform" variant read as an apology on
          the one tab (script_tag) that works on any site. */}

      {/* ADR-0212 Rule 3 — what THIS install covers, in the coverage
          card's four words, once above the panel. */}
      <InstallCoverageChips tab={active} detected={detected} />

      <div
        id={`install-tab-panel-${active}`}
        role="tabpanel"
        aria-labelledby={`install-tab-${active}`}
      >
        {active === 'script_tag' || active === 'custom_embed' ? (
          <ScriptTagInstallPanel
            {...(onInstalled !== undefined ? { onInstalled } : {})}
            {...(proofCheck !== undefined ? { proofCheck } : {})}
            redirectHost={redirectHostFromHints({
              hosting_hint: detected?.hosting_hint,
              vendor_hint: detected?.vendor_hint,
            })}
          />
        ) : null}
        {active === 'wordpress' ? (
          <WordPressInstallPanel
            {...(onInstalled !== undefined ? { onInstalled } : {})}
            {...(proofCheck !== undefined ? { proofCheck } : {})}
            onSwitchToScriptTab={() => switchTo('script_tag')}
            {...(showWorkerStep1
              ? { workerStep: <WorkerStepOne workerPanel={workerPanel(true)} deployed={cfDeployed} onPreferPlugin={() => setPreferPlugin(true)} testidPrefix="wp" showStepBadge={false} /> }
              : cfProxied ? { onPreferWorker: () => setPreferPlugin(false) } : {})}
          />
        ) : null}
        {active === 'woocommerce' ? (
          <WooCommerceInstallPanel
            {...(onInstalled !== undefined ? { onInstalled } : {})}
            {...(proofCheck !== undefined ? { proofCheck } : {})}
            onSwitchToScriptTab={() => switchTo('script_tag')}
            {...(showWorkerStep1
              ? { workerStep: <WorkerStepOne workerPanel={workerPanel(true)} deployed={cfDeployed} onPreferPlugin={() => setPreferPlugin(true)} testidPrefix="woo" /> }
              : cfProxied ? { onPreferWorker: () => setPreferPlugin(false) } : {})}
          />
        ) : null}
        {active === 'shopify' ? <ShopifyConnectPanel site={site} /> : null}
        {active === 'cloudflare' ? workerPanel(false) : null}
      </div>

      {/* ADR-0193 §9.6 — Cloudflare-proxied enhancement. Rendered BELOW
          the primary install as a clearly OPTIONAL, subordinate step
          (ghost action, not a competing lime CTA — founder 2026-07-24).
          It names what the primary method already does, the one gap it
          leaves, and the single thing the Worker adds — so the merchant
          understands why both, not just "two buttons." Only on shopify
          (optional, after connecting) and script_tag (recommended — the
          one tab where the Worker genuinely REPLACES the artifact).
          ADR-0212: on wordpress / woocommerce the Worker swaps into
          Step 1 itself (WorkerStepOne), so no pivot there. */}
      {/* ADR-0211 — grey-clouded serving record: the zone is on
          Cloudflare but the pages bypass it (openbuild.ai). One DNS
          toggle from the Worker; say so instead of silently falling
          back to the script tag. */}
      {/* Script-tag tab ONLY: the plugin already covers WordPress /
          WooCommerce regardless of the cloud colour, and Shopify has its
          own grey-cloud step (ShopifyCloudflareObserveStep) — showing this there would read as "set up Cloudflare
          instead of the plugin / app", which is exactly wrong. */}
      {detected?.apex_cloudflare === true && detected.cloudflare_proxied !== true && active === 'script_tag' ? (
        <div
          className="mt-5 pt-4"
          style={{ borderTop: `1px solid ${C.borderSoft}` }}
          data-testid="cf-grey-cloud-pivot"
        >
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: 2, color: C.amberFg, marginBottom: 8 }}>
            YOUR DNS IS ON CLOUDFLARE · BUT YOUR PAGES BYPASS IT
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 500, color: C.text, marginBottom: 6 }}>
            One DNS toggle away from the Worker
          </div>
          <p style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, lineHeight: 1.6, margin: '0 0 12px', maxWidth: 520 }}>
            {site.domain} is on Cloudflare, but the record that serves your pages (usually www) has a grey cloud, so Cloudflare — and the Sill Worker — never see them. In Cloudflare → DNS → Records, turn Proxy status on for that record. Then the Worker covers every crawler, serves your discovery files and adds the script for you.
          </p>
          <button
            type="button"
            onClick={() => switchTo('cloudflare')}
            className="inline-flex items-center gap-1.5 px-3 py-2"
            style={{ background: 'transparent', color: C.text, border: `1px solid ${C.border}`, borderRadius: 2, fontFamily: sansFont, fontWeight: 500, fontSize: 12.5, cursor: 'pointer' }}
            data-testid="cf-grey-cloud-pivot-switch"
          >
            I turned it on — set up the Worker <ArrowRight size={13} />
          </button>
        </div>
      ) : null}

      {/* 2026-10-28 — Shopify + Cloudflare O2O: with the store record
          proxied, the Worker can run in front of Shopify and observe every
          agent without the app. Shown on every Shopify site as an optional
          disclosure: Shopify's own Cloudflare answers every store, so
          headers cannot tell whether the merchant's DNS is on Cloudflare. */}
      {active === 'shopify' ? (
        <ShopifyCloudflareObserveStep onSetUpWorker={() => switchTo('cloudflare')} />
      ) : null}

      {/* Shopify: only once the Worker passes Shopify's own files, checkout
          and cart straight through (SHOPIFY_SAFE_WORKER_AVAILABLE). Until
          then the app and the /apps/sill steps are the Shopify path. */}
      {detected?.cloudflare_proxied === true
        && ((active === 'shopify' && SHOPIFY_SAFE_WORKER_AVAILABLE) || active === 'script_tag') ? (
        <div
          className="mt-5 pt-4"
          style={{ borderTop: `1px solid ${C.borderSoft}` }}
          data-testid="cf-proxy-pivot"
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              letterSpacing: 2,
              color: C.muted,
              marginBottom: 8,
            }}
          >
            {active === 'shopify'
              ? 'OPTIONAL · AFTER CONNECTING SHOPIFY'
              : 'RECOMMENDED · YOUR SITE RUNS THROUGH CLOUDFLARE'}
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              fontWeight: 500,
              color: C.text,
              marginBottom: 6,
            }}
          >
            {active === 'shopify'
              ? 'Add the one file Shopify can’t serve'
              : 'Use the Worker instead of the script'}
          </div>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 12.5,
              color: C.muted,
              lineHeight: 1.6,
              margin: '0 0 12px',
              maxWidth: 520,
            }}
          >
            {active === 'shopify'
              ? 'The app covers everything Shopify allows. The one path Shopify reserves is /.well-known/agent-card.json; since your store domain runs through Cloudflare, a Worker can serve it there too. Do this after connecting — it adds to the app, it doesn’t replace it.'
              : 'The script only sees visitors who run JavaScript — most AI agents don’t. Your site runs through Cloudflare, so the Sill Worker can see every request, serve your agent card and files, and add this script for you. One install, nothing to paste.'}
          </p>
          <button
            type="button"
            onClick={() => switchTo('cloudflare')}
            className="inline-flex items-center gap-1.5 px-3 py-2"
            style={{
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              fontFamily: sansFont,
              fontWeight: 500,
              fontSize: 12.5,
              cursor: 'pointer',
            }}
            data-testid="cf-proxy-pivot-switch"
          >
            Set up the Cloudflare Worker <ArrowRight size={13} />
          </button>
        </div>
      ) : null}

      {/* The pre-install "watch an agent" demo — relocated to the panel
          footer (founder 2026-07-22: it's a distraction next to the
          install choice, so it lives at the bottom, de-emphasized). */}
      {onWatchDemo !== undefined ? (
        <div
          className="mt-5 pt-4"
          style={{ borderTop: `1px solid ${C.borderSoft}` }}
        >
          <button
            type="button"
            onClick={onWatchDemo}
            className="inline-flex items-center gap-1.5"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.textDecoration = 'underline';
              el.style.color = C.limeFg;
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.textDecoration = 'none';
              el.style.color = C.muted;
            }}
          >
            <Play size={12} /> See an agent try it first
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * ADR-0197 model presentation
 *
 * Detected setup → recommended one-click action(s), always-visible
 * manual below. Cloudflare is a *property* (`cloudflare_proxied`), not
 * a peer tab — when true it ADDS a "Deploy the Worker" recommendation
 * on top of a static/custom site; a plugin platform ignores it (the
 * plugin already serves the static card).
 *
 * Recommendation matrix:
 *   WooCommerce → woo    (WooCommerceInstallPanel — a STORE: plugin
 *                         discovery + connect the store)
 *   WordPress   → plugin (AgentReadyPluginDownload)
 *   Shopify     → app    (ShopifyConnectPanel)  [+ worker if CF-proxied]
 *   cloudflare_proxied (non-store) → worker (CloudflareInstallPanel)
 *   Static / custom, no CF  → none — manual IS the primary
 *
 * Every case renders `ManualInstallSteps` below — the manual path is
 * ALWAYS available and consistent across platforms (founder 2026-07-27).
 * ───────────────────────────────────────────────────────────────────── */

type RecommendedKind = 'plugin' | 'woo' | 'worker' | 'app';

interface InstallModelProps {
  readonly detected: DetectPlatformResult | null;
  readonly site: Site;
  readonly proofCheck?: UseProofCheckState;
  readonly onInstalled?: () => void;
  readonly onWatchDemo?: () => void;
  readonly onSeeAllOptions: () => void;
  readonly onDomainVerified?: () => void;
}

/** Which recommended blocks to render on top of the manual path, in
 *  order. Pure function of the detection result. */
export function computeRecommendedBlocks(
  detected: DetectPlatformResult | null,
): readonly RecommendedKind[] {
  const platform = detected?.platform;
  const cf = detected?.cloudflare_proxied === true;
  const blocks: RecommendedKind[] = [];
  if (platform === 'woocommerce') {
    // A STORE — two must-do jobs: discovery (plugin) + connect the store
    // (WooCommerce REST keys) so agents can read the catalog + orders.
    // `WooCommerceInstallPanel` renders both as a numbered flow. CF is
    // irrelevant (the plugin serves the static card); the Worker would
    // double-count crawler logging (ADR-0197 §2/§5).
    blocks.push('woo');
  } else if (platform === 'wordpress') {
    // The plugin already does everything (card + proof + script + crawler
    // log). Cloudflare is irrelevant here — the Worker would just
    // double-count crawler logging (ADR-0197 §2). Plugin only.
    blocks.push('plugin');
  } else {
    if (platform === 'shopify') blocks.push('app');
    // Worker: the one-click path for static/custom sites behind Cloudflare
    // — and for Shopify on Cloudflare O2O it serves the static card Shopify
    // itself can't. Only offered when CF-proxy is actually detected.
    if (cf) blocks.push('worker');
  }
  return blocks;
}

/** Short human label for the detected setup — shown as the lead line
 *  above the recommendation block. Replaces the classic detection strip
 *  in `InstallVerifyStep` (skipped in model mode). */
function detectedSetupLabel(
  detected: DetectPlatformResult | null,
): { readonly kicker: string; readonly title: string } {
  const platform = detected?.platform;
  const cf = detected?.cloudflare_proxied === true;
  const platformName =
    platform === 'wordpress'
      ? 'WordPress'
      : platform === 'woocommerce'
        ? 'WooCommerce'
        : platform === 'shopify'
          ? 'Shopify'
          : platform === 'other_known'
              || platform === 'unknown'
              || platform === undefined
            ? 'Custom site'
            : 'Cloudflare';
  const suffix = cf && platform !== 'cloudflare' ? ' · Cloudflare' : '';
  return { kicker: 'WE THINK THIS IS', title: `${platformName}${suffix}` };
}

function InstallModel({
  detected,
  site,
  proofCheck,
  onInstalled,
  onWatchDemo,
  onSeeAllOptions,
  onDomainVerified,
}: InstallModelProps): JSX.Element {
  const C = useC();
  const blocks = computeRecommendedBlocks(detected);
  const label = detectedSetupLabel(detected);
  // Shopify manual variant: swap the static-card download for an O2O
  // guide link (Shopify blocks /.well-known/). ADR-0197 §4.
  const manualVariant: 'default' | 'shopify' =
    detected?.platform === 'shopify' ? 'shopify' : 'default';

  return (
    <div>
      {/* Lead line — the model's own detection callout. Replaces the
          classic detection strip in `InstallVerifyStep`. */}
      <div
        className="flex items-baseline gap-2 flex-wrap mb-4"
        data-testid="install-model-lead"
      >
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 2,
            color: C.muted,
          }}
        >
          {label.kicker}
        </span>
        <span
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            fontWeight: 500,
            color: C.text,
          }}
        >
          {label.title}
        </span>
      </div>

      {/* Recommended one-click action(s). When there is no recommendation
          (static / custom, no CF), this section is absent — manual is the
          primary. */}
      {blocks.length > 0 ? (
        <div className="mb-4" data-testid="install-model-recommended">
          {blocks.map((kind) => {
            if (kind === 'plugin') {
              return (
                <div key={kind} data-testid="install-model-action-plugin">
                  <AgentReadyPluginDownload />
                </div>
              );
            }
            if (kind === 'woo') {
              // A STORE — the WooCommerce panel is a self-contained
              // numbered flow (Step 1 plugin discovery; Step 2 connect
              // the store → catalog + orders). ADR-0197 §5.
              return (
                <div key={kind} data-testid="install-model-action-woo">
                  <WooCommerceInstallPanel
                    {...(onInstalled !== undefined ? { onInstalled } : {})}
                    {...(proofCheck !== undefined ? { proofCheck } : {})}
                  />
                </div>
              );
            }
            if (kind === 'worker') {
              return (
                <div
                  key={kind}
                  className="mb-4 p-4"
                  style={{
                    border: `1px solid ${C.limeFg}`,
                    borderRadius: 2,
                    background: C.limeDim,
                  }}
                  data-testid="install-model-action-worker"
                >
                  <div
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      letterSpacing: 2,
                      color: C.limeFg,
                      marginBottom: 8,
                    }}
                  >
                    RECOMMENDED
                  </div>
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 14,
                      fontWeight: 500,
                      color: C.text,
                      marginBottom: 4,
                    }}
                  >
                    Deploy the Cloudflare Worker
                  </div>
                  <p
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.muted,
                      lineHeight: 1.6,
                      marginBottom: 12,
                      maxWidth: 520,
                    }}
                  >
                    {WORKER_DOES_BODY}
                  </p>
                  <CloudflareInstallPanel
                    site={site}
                    {...(proofCheck !== undefined ? { proofCheck } : {})}
                    {...(onDomainVerified !== undefined ? { onDomainVerified } : {})}
                  />
                </div>
              );
            }
            // Shopify app
            return (
              <div
                key={kind}
                className="mb-4 p-4"
                style={{
                  border: `1px solid ${C.limeFg}`,
                  borderRadius: 2,
                  background: C.limeDim,
                }}
                data-testid="install-model-action-app"
              >
                <div
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    letterSpacing: 2,
                    color: C.limeFg,
                    marginBottom: 8,
                  }}
                >
                  RECOMMENDED
                </div>
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 14,
                    fontWeight: 500,
                    color: C.text,
                    marginBottom: 4,
                  }}
                >
                  Install the Sill app
                </div>
                <p
                  style={{
                    fontFamily: sansFont,
                    fontSize: 13,
                    color: C.muted,
                    lineHeight: 1.6,
                    marginBottom: 12,
                    maxWidth: 520,
                  }}
                >
                  Connect Shopify — one action installs the embed, proves
                  you own the domain, connects your store so agents can
                  read the catalog and orders, and serves your signed
                  agent card on your store domain.
                </p>
                <ShopifyConnectPanel site={site} />
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Manual — ALWAYS visible below, consistent across every platform.
          Some merchants prefer manual, or have no Cloudflare access.
          Never hidden behind a disclosure (founder 2026-07-27). */}
      <ManualInstallSteps
        {...(proofCheck !== undefined ? { proofCheck } : {})}
        variant={manualVariant}
      />

      {/* ADR-0042 reachability affordance — every method stays reachable
          via the classic tabs picker. */}
      <div className="mt-4">
        <button
          type="button"
          onClick={onSeeAllOptions}
          className="inline-flex items-center gap-1.5"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
          }}
          data-testid="install-model-see-all-options"
        >
          Not {label.title.toLowerCase()}? See all install options{' '}
          <ArrowRight size={12} />
        </button>
      </div>

      {/* Pre-install "watch an agent" demo — same footer position as the
          classic tabs render. */}
      {onWatchDemo !== undefined ? (
        <div
          className="mt-5 pt-4"
          style={{ borderTop: `1px solid ${C.borderSoft}` }}
        >
          <button
            type="button"
            onClick={onWatchDemo}
            className="inline-flex items-center gap-1.5"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
            }}
          >
            <Play size={12} /> See an agent try it first
          </button>
        </div>
      ) : null}
    </div>
  );
}
